import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Wallet, CheckCircle2, AlertCircle, Shield, QrCode } from 'lucide-react';
import { useWallet } from '../contexts/WalletContext';
import { useIsCallerAdmin } from '../hooks/useQueries';
import WalletConnectQRModal from './WalletConnectQRModal';
import { getWalletConnectQRUri, onWalletConnectDisplayUri } from '../lib/walletConnect';
import { toast } from 'sonner';

interface WalletConnectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function WalletConnectionModal({ open, onOpenChange }: WalletConnectionModalProps) {
  const { connect, isConnecting, connectionError, networkInfo, isICReady, icInitMessage } = useWallet();
  const { data: isAdmin } = useIsCallerAdmin();
  const [selectedWallet, setSelectedWallet] = useState<'web3' | 'ravencoin' | null>(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrUri, setQrUri] = useState<string | null>(null);
  

  useEffect(() => {
    if (!open) {
      setSelectedWallet(null);
      setShowQRModal(false);
      setQrUri(null);
    } else {
      // Listen for WalletConnect QR URI
      const unsubscribe = onWalletConnectDisplayUri((uri: string) => {
        setQrUri(uri);
        setShowQRModal(true);
      });

      // Check if QR URI already exists
      const existingUri = getWalletConnectQRUri();
      if (existingUri) {
        setQrUri(existingUri);
        setShowQRModal(true);
      }

      return () => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      };
    }
  }, [open]);
  

  const handleConnect = async (walletType: 'web3' | 'ravencoin') => {
    setSelectedWallet(walletType);
    try {
      // On mobile, keep modal open longer to show QR code
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      await connect(walletType);
      
      // On mobile, wait a bit before closing to ensure QR/deep link is visible
      if (isMobile && walletType === 'web3') {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      onOpenChange(false);
    } catch (error) {
      console.error('Connection failed:', error);
      // Don't close modal on error - let user retry
    }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Connect Wallet
          </DialogTitle>
          <DialogDescription>
            Choose your wallet to connect to KawSwap DEX
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* IC Initialization Status */}
          {!isICReady && icInitMessage && (
            <Alert className="border-purple-500/50 bg-purple-500/10">
              <Loader2 className="h-4 w-4 animate-spin text-purple-400" />
              <AlertDescription className="text-sm text-purple-200">
                {icInitMessage}
              </AlertDescription>
            </Alert>
          )}

          {/* Network Status */}
          {networkInfo && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/30">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-400" />
                <span className="text-sm font-medium text-green-200">Live Mainnet</span>
              </div>
              <Badge variant="outline" className="border-green-500/50 text-green-300">
                {networkInfo.networkName}
              </Badge>
            </div>
          )}

          {/* Admin Badge */}
          {isAdmin && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
              <Shield className="h-4 w-4 text-purple-400" />
              <span className="text-sm font-medium text-purple-200">Admin Wallet Verified</span>
            </div>
          )}

          {/* Connection Error */}
          {connectionError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                {connectionError}
                {connectionError.includes('CORS') || connectionError.includes('localhost') ? (
                  <div className="mt-2 text-xs text-muted-foreground">
                    💡 Tip: You can still use RVN swaps by entering your address manually in the swap interface.
                  </div>
                ) : null}
              </AlertDescription>
            </Alert>
          )}

          {/* WalletConnect v2 Info - Only show when WalletConnect is selected */}
          {selectedWallet === 'web3' && (
            <Alert className="border-blue-500/50 bg-blue-500/10 py-2">
              <QrCode className="h-3.5 w-3.5 text-blue-400 shrink-0 mt-0.5" />
              <AlertDescription className="text-xs text-blue-200 leading-tight">
                WalletConnect v2 - Works with browser extensions, mobile apps, and browsers.
                {qrUri && ' QR code available for mobile.'}
              </AlertDescription>
            </Alert>
          )}

          {/* Wallet Options */}
          <div className="space-y-2">
            {/* WalletConnect - First Option */}
            <Button
              onClick={() => handleConnect('web3')}
              disabled={isConnecting || !isICReady}
              className="w-full justify-start items-center gap-2.5 py-2.5 px-3 rounded-lg border border-purple-500/30 bg-gradient-to-r from-purple-600/90 to-purple-700/90 hover:from-purple-700 hover:to-purple-800 text-left shadow-md transition-all min-h-[60px]"
            >
              {isConnecting && selectedWallet === 'web3' ? (
                <Loader2 className="h-4 w-4 animate-spin text-purple-100 shrink-0" />
              ) : (
                <Wallet className="h-4 w-4 text-purple-100 shrink-0" />
              )}
              <div className="flex-1 min-w-0 overflow-hidden">
                <div className="font-semibold text-sm text-white mb-0.5 truncate">WalletConnect (v2)</div>
                <p className="text-[10px] text-purple-100/70 truncate leading-tight">
                  MetaMask, Trust Wallet, Coinbase, or WalletConnect
                </p>
              </div>
              <Badge variant="outline" className="shrink-0 border-purple-300/50 text-purple-100 text-[9px] px-1.5 py-0.5 whitespace-nowrap">
                v2
              </Badge>
            </Button>

            {/* RavenCore - Second Option */}
            <Button
              onClick={() => handleConnect('ravencoin')}
              disabled={isConnecting || !isICReady}
              className="w-full justify-start items-center gap-2.5 py-2.5 px-3 rounded-lg border border-orange-500/30 bg-gradient-to-r from-orange-600/90 to-orange-700/90 hover:from-orange-700 hover:to-orange-800 text-left shadow-md transition-all min-h-[60px]"
            >
              {isConnecting && selectedWallet === 'ravencoin' ? (
                <Loader2 className="h-4 w-4 animate-spin text-orange-100 shrink-0" />
              ) : (
                <Wallet className="h-4 w-4 text-orange-100 shrink-0" />
              )}
              <div className="flex-1 min-w-0 overflow-hidden">
                <div className="font-semibold text-sm text-white mb-0.5 truncate">Ravencoin Core</div>
                <p className="text-[10px] text-orange-100/70 truncate leading-tight">
                  Local Ravencoin Core wallet (localhost:8766)
                </p>
              </div>
              <Badge variant="outline" className="shrink-0 border-orange-300/50 text-orange-100 text-[9px] px-1.5 py-0.5 whitespace-nowrap">
                RVN
              </Badge>
            </Button>

            {/* REMOVED: ICP, Solana, XRP - Code kept for future use when aggregators support them */}
            {/* 
            <Button
              onClick={() => handleConnect('icp')}
              disabled={isConnecting || !isICReady}
              className="w-full justify-start items-center gap-2.5 py-2.5 px-3 rounded-lg border border-blue-500/30 bg-gradient-to-r from-blue-600/90 to-blue-700/90 hover:from-blue-700 hover:to-blue-800 text-left shadow-md transition-all min-h-[60px]"
            >
              {isConnecting && selectedWallet === 'icp' ? (
                <Loader2 className="h-4 w-4 animate-spin text-blue-100 shrink-0" />
              ) : (
                <Wallet className="h-4 w-4 text-blue-100 shrink-0" />
              )}
              <div className="flex-1 min-w-0 overflow-hidden">
                <div className="font-semibold text-sm text-white mb-0.5 truncate">Connect ICP Wallet</div>
                <p className="text-[10px] text-blue-100/70 truncate leading-tight">
                  Internet Identity (Plug, Stoic, NFID, etc.)
                </p>
              </div>
              <Badge variant="outline" className="shrink-0 border-blue-300/50 text-blue-100 text-[9px] px-1.5 py-0.5 whitespace-nowrap">
                All Wallets
              </Badge>
            </Button>

            <Button
              onClick={() => handleConnect('solana')}
              disabled={isConnecting || !isICReady}
              className="w-full justify-start items-center gap-2.5 py-2.5 px-3 rounded-lg border border-green-500/30 bg-gradient-to-r from-green-600/90 to-green-700/90 hover:from-green-700 hover:to-green-800 text-left shadow-md transition-all min-h-[60px]"
            >
              {isConnecting && selectedWallet === 'solana' ? (
                <Loader2 className="h-4 w-4 animate-spin text-green-100 shrink-0" />
              ) : (
                <Wallet className="h-4 w-4 text-green-100 shrink-0" />
              )}
              <div className="flex-1 min-w-0 overflow-hidden">
                <div className="font-semibold text-sm text-white mb-0.5 truncate">Connect Solana Wallet</div>
                <p className="text-[10px] text-green-100/70 truncate leading-tight">
                  Phantom, Solflare, Backpack, etc.
                </p>
              </div>
              <Badge variant="outline" className="shrink-0 border-green-300/50 text-green-100 text-[9px] px-1.5 py-0.5 whitespace-nowrap">
                All SOL
              </Badge>
            </Button>

            <Button
              onClick={() => handleConnect('xrp')}
              disabled={isConnecting || !isICReady}
              className="w-full justify-start items-center gap-2.5 py-2.5 px-3 rounded-lg border border-cyan-500/30 bg-gradient-to-r from-cyan-600/90 to-cyan-700/90 hover:from-cyan-700 hover:to-cyan-800 text-left shadow-md transition-all min-h-[60px]"
            >
              {isConnecting && selectedWallet === 'xrp' ? (
                <Loader2 className="h-4 w-4 animate-spin text-cyan-100 shrink-0" />
              ) : (
                <Wallet className="h-4 w-4 text-cyan-100 shrink-0" />
              )}
              <div className="flex-1 min-w-0 overflow-hidden">
                <div className="font-semibold text-sm text-white mb-0.5 truncate">Connect XRP Wallet</div>
                <p className="text-[10px] text-cyan-100/70 truncate leading-tight">
                  Xumm, Gem Wallet, Crossmark, etc.
                </p>
              </div>
              <Badge variant="outline" className="shrink-0 border-cyan-300/50 text-cyan-100 text-[9px] px-1.5 py-0.5 whitespace-nowrap">
                All XRP
              </Badge>
            </Button>
            */}
          </div>

          {/* Info Text */}
          <p className="text-xs text-gray-400 text-center mt-4">
            All connections are secured and use live mainnet RPC endpoints.
            {!isICReady && ' Please wait for initialization to complete.'}
          </p>
        </div>
      </DialogContent>
      
      {/* WalletConnect QR Code Modal */}
      <WalletConnectQRModal open={showQRModal} onOpenChange={setShowQRModal} />
    </Dialog>
  );
}
